import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useDispatch } from 'react-redux';

import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Loader } from '../components/ui/Loader';
import {
  useGetProxyDetailsQuery,
  useGetServer3UpdateOptionsQuery,
  useGetProxyExtendPriceQuery,
  useGetProxy5gExtendPriceQuery,
  useRenewProxyMutation,
  useUpdateServer3ProxyMutation,
  useModifyProxy5gMutation,
  useAddIpToProxy5gMutation,
  useRemoveIpFromProxy5gMutation,
  useChangeProxy5gPackageMutation,
  useGetFullProxy5gDetailsQuery,
  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  useGetServer4ParentProxiesQuery,
  useGetServer4ProvidersQuery,
  useCheckProxy5gUsernameQuery,
  useValidateParentProxyQuery,
} from '../store/api/proxiesApi';
import { baseApi } from '../store/api/baseApi';

import type { Server4Options, Server4Package, Server4Country, Server4City, Server4ParentProxy, Server4Provider } from '../types/api';
import { formatDate } from '../utils/dateFormatter';
import { useLanguage } from '../hooks/useLanguage';
import { useWizardBackNav } from '../hooks/useWizardBackNav';

const getFlagEmoji = (countryCode?: string | null): string => {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const PROTOCOL_COOLDOWN_MS = 5 * 60 * 1000;

function formatRemainingTime(expiresAt: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  if (diffMs <= 0) return t('proxies.details.expiredLabel');
  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 1) return t('proxies.details.timeLessThanMinute');
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return t('proxies.details.timeRemainingDhm', { days, hours, minutes });
  if (hours > 0) return t('proxies.details.timeRemainingHm', { hours, minutes });
  return t('proxies.details.timeRemainingM', { minutes });
}

function mapProxy5gError(err: unknown, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const raw = (err as { data?: { message?: string }; message?: string })?.data?.message
    || (err as { message?: string })?.message || '';
  if (raw.includes('Failed to switch conn type')) return t('proxies.details.protocolChangeFailed', { protocol: '' });
  if (raw.includes('does not support specified protocol')) return t('proxies.details.protocolNotSupported', { protocol: '' });
  if (raw.includes('No available matching proxy') || raw.includes('no_available_proxy')) return t('proxies.details.noAvailableProxy');
  if (raw.includes('503')) return t('proxies.details.serviceUnavailable');
  return raw || t('proxies.purchase.error');
}

export function ProxyDetailsPage() {
  const { proxyId } = useParams<{ proxyId: string }>();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRenewDays, setSelectedRenewDays] = useState<number | ''>('');
  const [selectedRenewMonths, setSelectedRenewMonths] = useState<number>(1);
  const [selectedRenewDays5g, setSelectedRenewDays5g] = useState<string>('7d');
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedRegionCode, setSelectedRegionCode] = useState('');
  const [selectedCarrierCode, setSelectedCarrierCode] = useState('');
  const [modifyPassword, setModifyPassword] = useState('');
  const [modifyIpAddresses, setModifyIpAddresses] = useState('');
  const [modifyProtocol, setModifyProtocol] = useState('');
  const [addIpAddress, setAddIpAddress] = useState('');
  const [changePkgId, setChangePkgId] = useState<number | ''>('');
  const [changePkgIpAddr, setChangePkgIpAddr] = useState('');
  const [changePkgUsername, setChangePkgUsername] = useState('');
  const [changePkgPassword, setChangePkgPassword] = useState('');
  const [changePkgProtocol, setChangePkgProtocol] = useState('http');
  const [changePkgUsernameToCheck, setChangePkgUsernameToCheck] = useState('');
  const [pkgChangeStep, setPkgChangeStep] = useState<'idle' | 'country' | 'city' | 'serviceProvider' | 'parentProxy' | 'credentials'>('idle');
  const [pkgChangeCountry, setPkgChangeCountry] = useState('');
  const [pkgChangeCity, setPkgChangeCity] = useState('');
  const [pkgChangeServiceProvider, setPkgChangeServiceProvider] = useState('');
  const [pkgChangeParentProxy, setPkgChangeParentProxy] = useState('');
  const [pkgChangeCountrySearch, setPkgChangeCountrySearch] = useState('');
  const [pkgChangeCitySearch, setPkgChangeCitySearch] = useState('');
  const [pkgChangeProtocol, setPkgChangeProtocol] = useState('http');
  const [pkgChangeUsername, setPkgChangeUsername] = useState('');
  const [pkgChangePassword, setPkgChangePassword] = useState('');
  const [pkgChangeIpAddr, setPkgChangeIpAddr] = useState('');
  const [pkgChangeUsernameToCheck, setPkgChangeUsernameToCheck] = useState('');
  const [locationStep, setLocationStep] = useState<'idle' | 'protocol' | 'country' | 'city' | 'serviceProvider' | 'parentProxy'>('idle');
  const [locationProtocol, setLocationProtocol] = useState('http');
  const [locationCountry, setLocationCountry] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationServiceProvider, setLocationServiceProvider] = useState('');
  const [locationParentProxy, setLocationParentProxy] = useState('');
  const locationParentProxyRef = useRef('');
  useEffect(() => { locationParentProxyRef.current = locationParentProxy; }, [locationParentProxy]);
  const [locationCountrySearch, setLocationCountrySearch] = useState('');
  const [locationCitySearch, setLocationCitySearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const { data, isLoading, error, refetch } = useGetProxyDetailsQuery(proxyId || '');
  const { data: server3OptionsData } = useGetServer3UpdateOptionsQuery(proxyId || '', {
    skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 3 || data?.data.proxy.status === 'expired',
  });
  const { data: extendPriceData, isFetching: isFetchingPrice } = useGetProxyExtendPriceQuery(
    { proxyId: proxyId || '', months: selectedRenewMonths },
    { skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 1 || data?.data.proxy.status === 'expired' },
  );
  const extend5gPeriod = selectedRenewDays5g.endsWith('h') ? `${parseInt(selectedRenewDays5g, 10)}h` : `${parseInt(selectedRenewDays5g, 10)}d`;
  const { data: extendPrice5gData, isFetching: isFetchingPrice5g } = useGetProxy5gExtendPriceQuery(
    { proxyId: proxyId || '', days: extend5gPeriod },
    { skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 4 || data?.data.proxy.status === 'expired' },
  );
  const [renewProxy, { isLoading: isRenewing }] = useRenewProxyMutation();
  const [updateServer3Proxy, { isLoading: isUpdatingServer3 }] = useUpdateServer3ProxyMutation();
  const [modifyProxy5g, { isLoading: isModifying5g }] = useModifyProxy5gMutation();
  const [addIpToProxy5g, { isLoading: isAddingIp5g }] = useAddIpToProxy5gMutation();
  const [removeIpFromProxy5g, { isLoading: isRemovingIp5g }] = useRemoveIpFromProxy5gMutation();
  const [changeProxy5gPackage, { isLoading: isChangingPkg5g }] = useChangeProxy5gPackageMutation();

  const { data: fullDetails5gData, isFetching: loadingFullDetails5g, refetch: refetch5gDetails } = useGetFullProxy5gDetailsQuery(
    proxyId || '',
    { skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 4 || data?.data.proxy.status === 'expired' },
  );

  const { data: server4OptionsData } = useGetServerOptionsQuery(4, {
    skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 4 || data?.data.proxy.status === 'expired',
  });

  const server4Options = server4OptionsData as Server4Options | undefined;
  const server4OptionsPayload = (server4Options as { data?: Server4Options })?.data ?? server4Options;
  const server4Packages: Server4Package[] = server4OptionsPayload?.options?.packages ?? [];
  const server4AllCountries: Server4Country[] = server4OptionsPayload?.options?.countries ?? [];
  const server4PackageCountriesMap = server4OptionsPayload?.options?.packageCountries;
  const server4Countries: Server4Country[] = (changePkgId && server4PackageCountriesMap)
    ? (server4PackageCountriesMap[changePkgId] ?? server4AllCountries)
    : server4AllCountries;
  const proxyPkgId = data?.data.proxy?.pkgId || 1;
  const locationCountries: Server4Country[] = (server4PackageCountriesMap && proxyPkgId)
    ? (server4PackageCountriesMap[proxyPkgId] ?? server4AllCountries)
    : server4AllCountries;

  const { data: locationNextOptionsData } = useGetServerNextOptionsQuery(
    { serverId: 4, params: { pkgId: String(proxyPkgId), countryId: locationCountry } },
    { skip: locationStep !== 'city' || !locationCountry },
  );
  const locationCitiesData = (locationNextOptionsData as { data?: { nextOptions?: { cities?: Server4City[] } } } | undefined)?.data ?? locationNextOptionsData;
  const locationCities: Server4City[] = (locationCitiesData as { nextOptions?: { cities?: Server4City[] } } | undefined)?.nextOptions?.cities ?? [];

  const { data: locationParentProxiesData, isLoading: loadingLocationProxies } = useGetServer4ParentProxiesQuery(
    { pkgId: Number(proxyPkgId), countryId: Number(locationCountry), cityId: locationCity ? Number(locationCity) : undefined, serviceProviderCityId: locationServiceProvider ? Number(locationServiceProvider) : undefined },
    { skip: locationStep !== 'parentProxy' || !locationCountry },
  );
  const rawLocationProxies = ((locationParentProxiesData as { data?: { parentProxies?: Server4ParentProxy[] } })?.data?.parentProxies ?? []);
  const locationParentProxies: Server4ParentProxy[] = rawLocationProxies.filter((p) => {
    if (locationProtocol === 'socks') return !!p.socksPort;
    return !!p.httpPort;
  });

  const { data: locationProvidersData, isLoading: loadingLocationProviders } = useGetServer4ProvidersQuery(
    { serverId: 4, cityId: Number(locationCity), pkgId: Number(proxyPkgId) },
    { skip: locationStep !== 'serviceProvider' || !locationCity },
  );
  const locationProviders: Server4Provider[] = (locationProvidersData as { data?: { providers?: Server4Provider[] } })?.data?.providers ?? [];

  useEffect(() => {
    if (locationStep === 'serviceProvider' && !loadingLocationProviders && locationProviders.length === 0) {
      setLocationServiceProvider('');
      setLocationStep('parentProxy');
    }
  }, [locationStep, loadingLocationProviders, locationProviders]);

  const { data: pkgChangeNextOptionsData } = useGetServerNextOptionsQuery(
    { serverId: 4, params: { pkgId: String(changePkgId || proxyPkgId), countryId: pkgChangeCountry } },
    { skip: pkgChangeStep !== 'city' || !pkgChangeCountry },
  );
  const pkgChangeCitiesData = (pkgChangeNextOptionsData as { data?: { nextOptions?: { cities?: Server4City[] } } } | undefined)?.data ?? pkgChangeNextOptionsData;
  const pkgChangeCities: Server4City[] = (pkgChangeCitiesData as { nextOptions?: { cities?: Server4City[] } } | undefined)?.nextOptions?.cities ?? [];

  const { data: pkgChangeParentProxiesData, isLoading: loadingPkgChangeProxies } = useGetServer4ParentProxiesQuery(
    { pkgId: Number(changePkgId || proxyPkgId), countryId: Number(pkgChangeCountry), cityId: pkgChangeCity ? Number(pkgChangeCity) : undefined, serviceProviderCityId: pkgChangeServiceProvider ? Number(pkgChangeServiceProvider) : undefined },
    { skip: pkgChangeStep !== 'parentProxy' || !pkgChangeCountry },
  );
  const rawPkgChangeProxies = ((pkgChangeParentProxiesData as { data?: { parentProxies?: Server4ParentProxy[] } })?.data?.parentProxies ?? []);
  const pkgChangeParentProxies: Server4ParentProxy[] = rawPkgChangeProxies;

  const { data: pkgChangeProvidersData, isLoading: loadingPkgChangeProviders } = useGetServer4ProvidersQuery(
    { serverId: 4, cityId: Number(pkgChangeCity), pkgId: Number(changePkgId || proxyPkgId) },
    { skip: pkgChangeStep !== 'serviceProvider' || !pkgChangeCity },
  );
  const pkgChangeProviders: Server4Provider[] = (pkgChangeProvidersData as { data?: { providers?: Server4Provider[] } })?.data?.providers ?? [];

  useEffect(() => {
    if (pkgChangeStep === 'serviceProvider' && !loadingPkgChangeProviders && pkgChangeProviders.length === 0) {
      setPkgChangeServiceProvider('');
      setPkgChangeStep('parentProxy');
    }
  }, [pkgChangeStep, loadingPkgChangeProviders, pkgChangeProviders]);

  const { data: pkgChangeUsernameCheckData, isFetching: pkgChangeCheckingUsername } = useCheckProxy5gUsernameQuery(
    { username: pkgChangeUsernameToCheck, pkgId: changePkgId ? Number(changePkgId) : 1 },
    { skip: !pkgChangeUsernameToCheck || changePkgId === '' },
  );
  const pkgChangeUsernameChecked = !!pkgChangeUsernameToCheck && !pkgChangeCheckingUsername && pkgChangeUsernameCheckData !== undefined;
  const pkgChangeResult = pkgChangeUsernameCheckData?.data?.result;
  const pkgChangeUsernameAvailable = pkgChangeUsernameChecked && pkgChangeUsernameToCheck === pkgChangeUsername.trim() && pkgChangeResult !== 'ALREADY_USER' && pkgChangeResult !== 'ALREADY_USED';
  const pkgChangeUsernameTaken = pkgChangeUsernameChecked && !pkgChangeUsernameAvailable;

  const { data: changePkgUsernameCheckData, isFetching: changePkgCheckingUsername } = useCheckProxy5gUsernameQuery(
    { username: changePkgUsernameToCheck, pkgId: changePkgId ? Number(changePkgId) : 1 },
    { skip: !changePkgUsernameToCheck || changePkgId === '' },
  );
  const changePkgUsernameAvailable = changePkgUsernameCheckData?.data?.result !== 'ALREADY_USER';
  const changePkgUsernameTaken = changePkgUsernameToCheck && !changePkgCheckingUsername && !changePkgUsernameAvailable;

  const userPassPackages = [1, 2, 4];
  const injectionPackages = [3, 5];
  const isUserPassPkg = (pkgId: number) => userPassPackages.includes(pkgId);
  const isInjectionPkg = (pkgId: number) => injectionPackages.includes(pkgId);
  const getPkgTypeName = (pkgId: number) => isInjectionPkg(pkgId) ? t('proxies.details.injectionPackage') : t('proxies.details.userPassPackage');
  const pkgDisplayNames: Record<number, string> = { 1: 'Premium Package', 2: 'Basic Package', 3: 'Premium Package Injection', 4: 'Ultimate Package', 5: 'Ultimate Package Injection' };
  const getPkgDisplayName = (pkg: Server4Package) => pkgDisplayNames[Number(pkg.id)] || pkg.name;
  const allowedSwitchMap: Record<number, number[]> = { 1: [3], 2: [], 3: [1], 4: [5], 5: [4] };

  const proxy = data?.data.proxy;
  const currentPkg = proxy?.pkgId || 1;
  const allowedTargets = allowedSwitchMap[currentPkg] || [];
  const allowedPackages = server4Packages.filter(p => allowedTargets.includes(Number(p.id)));
  const server3Options = server3OptionsData?.data;
  const lockDates = server3Options?.lockDates ?? proxy?.lockDates;

  const protocolCooldownRemaining = useMemo(() => {
    if (!proxy?.purchasedAt) return 0;
    const now = Date.now();
    const purchaseAt = new Date(proxy.purchasedAt).getTime();
    const earliestAllowed = purchaseAt + PROTOCOL_COOLDOWN_MS;
    const diff = earliestAllowed - now;
    return diff > 0 ? Math.ceil(diff / 60000) : 0;
  }, [proxy?.purchasedAt]);

  const isLockActive = (lockedUntil?: string | null) =>
    Boolean(lockedUntil && new Date(lockedUntil) > new Date());
  const lockedUntilText = (lockedUntil?: string | null) =>
    lockedUntil ? formatDate(lockedUntil, 'PP h:mm a', currentLanguage) : '';

  useEffect(() => {
    if (!server3Options) return;
    if (selectedRenewDays === '' && server3Options.renewalPeriods.length > 0) {
      setSelectedRenewDays(server3Options.renewalPeriods[0]!);
    }
    if (!selectedCountryCode && server3Options.options.countries.length > 0) {
      setSelectedCountryCode(server3Options.options.countries[0]!.code);
    }
    if (!selectedRegionCode && server3Options.options.states.length > 0) {
      setSelectedRegionCode(server3Options.options.states[0]!.code);
    }
    if (!selectedCarrierCode && server3Options.options.carriers.length > 0) {
      setSelectedCarrierCode(server3Options.options.carriers[0]!.code);
    }
  }, [server3Options, selectedRenewDays, selectedCountryCode, selectedRegionCode, selectedCarrierCode]);

  useEffect(() => {
    if (proxy?.type) {
      setModifyProtocol(proxy.type.toLowerCase());
    }
  }, [proxy?.type]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError') || t('errors.network'));
    }
  };

  const handleCopyConnectionString = () => {
    if (!proxy) return;
    const connectionString = `${proxy.username}:${proxy.password}@${proxy.ipAddress}:${proxy.port}`;
    handleCopy(connectionString);
  };

  const handleRenew = async () => {
    if (!proxy) return;
    const renewArgs: { proxyId: string; days?: number; months?: number; hours?: number } = { proxyId: proxy._id };
    if (proxy.serverId === 3 && selectedRenewDays !== '') {
      renewArgs.days = selectedRenewDays;
    } else if (proxy.serverId === 4) {
      const period = selectedRenewDays5g;
      if (period.endsWith('h')) renewArgs.hours = parseInt(period, 10);
      else renewArgs.days = parseInt(period, 10);
    } else if (proxy.serverId === 1) {
      renewArgs.months = selectedRenewMonths;
    }
    try {
      await renewProxy(renewArgs).unwrap();
      toast.success(t('proxies.details.renewSuccess'));
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies']));
      await new Promise((r) => setTimeout(r, 300));
      await refetch();
    } catch (err) {
      const message = (err as { data?: { message?: string }; message?: string })?.data?.message
        || (err as { message?: string })?.message
        || t('proxies.purchase.error');
      toast.error(message);
    }
  };

  const handleServer3Action = async (
    action: 'change_region' | 'change_country' | 'change_carrier' | 'change_proxy_type' | 'toggle_rotation',
    value?: string
  ) => {
    if (!proxy || proxy.serverId !== 3) return;
    try {
      if (action === 'change_region') {
        if (!value) return;
        await updateServer3Proxy({ proxyId: proxy._id, body: { action, regionCode: value } }).unwrap();
      } else if (action === 'change_country') {
        if (!value) return;
        await updateServer3Proxy({ proxyId: proxy._id, body: { action, countryCode: value } }).unwrap();
      } else if (action === 'change_carrier') {
        if (!value) return;
        await updateServer3Proxy({ proxyId: proxy._id, body: { action, carrier: value } }).unwrap();
      } else if (action === 'change_proxy_type') {
        const nextType = proxy.type.toUpperCase().includes('SOCKS') ? 'HTTPS' : 'SOCKS5';
        await updateServer3Proxy({
          proxyId: proxy._id,
          body: { action, proxyType: nextType },
        }).unwrap();
      } else {
        await updateServer3Proxy({ proxyId: proxy._id, body: { action } }).unwrap();
      }

      toast.success(t('proxies.details.updateSuccess'));
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies']));
      await new Promise((r) => setTimeout(r, 300));
      await refetch();
    } catch (err) {
      const message = (err as { data?: { message?: string }; message?: string })?.data?.message
        || (err as { message?: string })?.message
        || t('proxies.purchase.error');
      toast.error(message);
    }
  };

  const handleModifyProxy5g = async (actionType?: 'password' | 'protocol', protocolValue?: string) => {
    if (!proxy || proxy.serverId !== 4) return;
    try {
      const isInjection = proxy.pkgId && isInjectionPkg(Number(proxy.pkgId));
      const body: Record<string, string> = {};
      if (actionType === 'protocol' && protocolValue) {
        if (protocolCooldownRemaining > 0) {
          toast.error(t('proxies.details.protocolCooldown', { minutes: protocolCooldownRemaining }));
          return;
        }
        body.protocol = protocolValue;
      } else if (actionType === 'password') {
        if (!modifyPassword.trim()) return;
        body.password = modifyPassword.trim();
      } else {
        if (!isInjection && modifyPassword.trim()) body.password = modifyPassword.trim();
        if (isInjection && modifyIpAddresses.trim()) body.ipAddresses = modifyIpAddresses.trim();
        if (modifyProtocol) body.protocol = modifyProtocol;
      }
      if (Object.keys(body).length === 0) {
        toast.error(t('proxies.details.noChanges') || t('proxies.purchase.error'));
        return;
      }
      await modifyProxy5g({ proxyId: proxy._id, body }).unwrap();
      toast.success(t('proxies.details.updateSuccess'));
      if (actionType === 'password') {
        setModifyPassword('');
      } else if (actionType === 'protocol' && protocolValue) {
        setModifyProtocol(protocolValue);
      }
      setModifyIpAddresses('');
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies', 'Proxy5gDetails']));
      await new Promise((r) => setTimeout(r, 300));
      await refetch();
      await refetch5gDetails();
    } catch (err) {
      toast.error(mapProxy5gError(err, t));
    }
  };

  const handleAddIpToProxy5g = async () => {
    if (!proxy || proxy.serverId !== 4) return;
    const ip = addIpAddress.trim();
    if (!ip) {
      toast.error(t('proxies.details.enterIp') || 'Enter an IP address');
      return;
    }
    try {
      await addIpToProxy5g({ proxyId: proxy._id, body: { ip } }).unwrap();
      toast.success(t('proxies.details.ipAdded') || 'IP added successfully');
      setAddIpAddress('');
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies', 'Proxy5gDetails']));
      await new Promise((r) => setTimeout(r, 300));
      await refetch();
    } catch (err) {
      toast.error(mapProxy5gError(err, t));
    }
  };

  const handleRemoveIpFromProxy5g = async (ipIndex: number) => {
    if (!proxy || proxy.serverId !== 4) return;
    try {
      await removeIpFromProxy5g({ proxyId: proxy._id, body: { ipIndex } }).unwrap();
      toast.success(t('proxies.details.ipDeleted') || 'IP removed successfully');
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies', 'Proxy5gDetails']));
      await new Promise((r) => setTimeout(r, 300));
      await refetch();
    } catch (err) {
      toast.error(mapProxy5gError(err, t));
    }
  };

  const targetPkg = changePkgId !== '' ? Number(changePkgId) : null;
  const isConvertingToInjection = targetPkg && isInjectionPkg(targetPkg) && isUserPassPkg(currentPkg);

  useEffect(() => {
    if (changePkgId !== '' && !isConvertingToInjection) {
      setChangePkgProtocol(proxy?.type || 'http');
    }
  }, [changePkgId, isConvertingToInjection, proxy?.type]);

  const restoreLocationStep = useCallback((target: typeof locationStep) => {
    if (target === 'idle') {
      setLocationProtocol('http');
      setLocationCountry('');
      setLocationCity('');
      setLocationServiceProvider('');
      setLocationParentProxy('');
      setLocationCountrySearch('');
      setLocationCitySearch('');
    } else if (target === 'protocol') {
      setLocationCountry('');
      setLocationCity('');
      setLocationServiceProvider('');
      setLocationParentProxy('');
    } else if (target === 'country') {
      setLocationCity('');
      setLocationServiceProvider('');
      setLocationParentProxy('');
    } else if (target === 'city') {
      setLocationServiceProvider('');
      setLocationParentProxy('');
    } else if (target === 'serviceProvider') {
      setLocationParentProxy('');
    } else if (target === 'parentProxy') {
      setLocationParentProxy('');
    }
    setLocationStep(target);
  }, []);

  const restoreChangePkgStep = useCallback((target: typeof pkgChangeStep) => {
    if (target === 'idle') {
      setChangePkgId('');
      setPkgChangeStep('idle');
      setPkgChangeCountry('');
      setPkgChangeCity('');
      setPkgChangeServiceProvider('');
      setPkgChangeParentProxy('');
      setPkgChangeCountrySearch('');
      setPkgChangeCitySearch('');
      setPkgChangeProtocol('http');
      setPkgChangeUsername('');
      setPkgChangePassword('');
      setPkgChangeIpAddr('');
      setPkgChangeUsernameToCheck('');
    } else if (target === 'country') {
      setPkgChangeCountry('');
      setPkgChangeCity('');
      setPkgChangeServiceProvider('');
      setPkgChangeParentProxy('');
    } else if (target === 'city') {
      setPkgChangeCity('');
      setPkgChangeServiceProvider('');
      setPkgChangeParentProxy('');
    } else if (target === 'serviceProvider') {
      setPkgChangeServiceProvider('');
      setPkgChangeParentProxy('');
    } else if (target === 'parentProxy') {
      setPkgChangeParentProxy('');
    }
    setPkgChangeStep(target);
  }, []);

  const { goBack: handleLocationBack } = useWizardBackNav(
    locationStep,
    restoreLocationStep,
    `proxy5g-location-${proxyId ?? ''}`,
    proxy?.serverId === 4 && locationStep !== 'idle',
    'idle',
  );

  const { goBack: handleChangePkgBack } = useWizardBackNav(
    pkgChangeStep,
    restoreChangePkgStep,
    `proxy5g-package-${proxyId ?? ''}`,
    proxy?.serverId === 4 && pkgChangeStep !== 'idle',
    'idle',
  );

  const handleChangeProxy5gPackage = async () => {
    if (!proxy || proxy.serverId !== 4 || changePkgId === '' || !pkgChangeParentProxy) return;
    try {
      const targetIsInjection = isInjectionPkg(Number(changePkgId));
      const body: Record<string, string | number> = {
        newPkgId: Number(changePkgId),
        parentProxyId: Number(pkgChangeParentProxy),
        protocol: targetIsInjection ? 'http' : (pkgChangeProtocol || proxy.type || 'http'),
      };
      if (targetIsInjection && pkgChangeIpAddr.trim()) {
        body.ipAddr = pkgChangeIpAddr.trim();
      } else if (!targetIsInjection) {
        if (pkgChangeUsername.trim()) body.username = pkgChangeUsername.trim();
        if (pkgChangePassword.trim()) body.password = pkgChangePassword.trim();
      }
      await changeProxy5gPackage({ proxyId: proxy._id, body }).unwrap();
      toast.success(t('proxies.details.updateSuccess'));
      setPkgChangeStep('idle');
      setChangePkgId('');
      setPkgChangeCountry('');
      setPkgChangeCity('');
      setPkgChangeServiceProvider('');
      setPkgChangeParentProxy('');
      setPkgChangeIpAddr('');
      setPkgChangeUsername('');
      setPkgChangePassword('');
      setPkgChangeUsernameToCheck('');
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies', 'Proxy5gDetails']));
      await new Promise((r) => setTimeout(r, 500));
      await refetch();
      await refetch5gDetails();
    } catch (err) {
      toast.error(mapProxy5gError(err, t));
    }
  };

  const handleLocationChange = async () => {
    const pp = locationParentProxyRef.current;
    if (!proxy || proxy.serverId !== 4 || !pp) return;

    const selectedPp = locationParentProxies.find((p) => String(p.id) === pp);
    if (selectedPp) {
      const supportsHttp = !!selectedPp.httpPort;
      const supportsSocks = !!selectedPp.socksPort;
      if (locationProtocol === 'socks' && !supportsSocks) {
        const available = [supportsHttp ? 'HTTP' : null, supportsSocks ? 'SOCKS' : null].filter(Boolean).join('/');
        toast.error(t('proxies.details.protocolNotSupportedByParent', { protocol: 'SOCKS', available }));
        return;
      }
      if (locationProtocol === 'http' && !supportsHttp) {
        const available = [supportsHttp ? 'HTTP' : null, supportsSocks ? 'SOCKS' : null].filter(Boolean).join('/');
        toast.error(t('proxies.details.protocolNotSupportedByParent', { protocol: 'HTTP', available }));
        return;
      }
    }

    try {
      const body: import('../types/api').ModifyProxy5gBody = {
        parentProxyId: pp,
        protocol: locationProtocol,
      };
      if (locationServiceProvider) body.serviceProviderCityId = Number(locationServiceProvider);
      await modifyProxy5g({ proxyId: proxy._id, body }).unwrap();
      toast.success(t('proxies.details.updateSuccess'));
      setLocationStep('idle');
      setLocationProtocol('http');
      setLocationCountry('');
      setLocationCity('');
      setLocationServiceProvider('');
      setLocationParentProxy('');
      dispatch(baseApi.util.invalidateTags([{ type: 'Proxy', id: proxy._id }, 'Proxies', 'Proxy5gDetails']));
      await new Promise((r) => setTimeout(r, 300));
      await refetch();
    } catch (err) {
      const raw = (err as { data?: { message?: string }; message?: string })?.data?.message
        || (err as { message?: string })?.message || '';
      if (raw.includes('Failed to switch conn type')) {
        toast.error(t('proxies.details.protocolChangeFailed', { protocol: locationProtocol.toUpperCase() }));
      } else if (raw.includes('does not support specified protocol')) {
        toast.error(t('proxies.details.protocolNotSupported', { protocol: locationProtocol.toUpperCase() }));
      } else if (raw.includes('503')) {
        toast.error(t('proxies.details.serviceUnavailable'));
      } else if (raw.includes('No available matching proxy') || raw.includes('no_available_proxy')) {
        toast.error(t('proxies.details.noAvailableProxy'));
      } else {
        toast.error(raw || t('proxies.purchase.error'));
      }
    }
  };

  const handleChangePkgCheckUsername = () => {
    if (changePkgUsername.trim()) {
      setChangePkgUsernameToCheck(changePkgUsername.trim());
    }
  };

  const requestConfirm = (title: string, message: string, action: () => void) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-5xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !proxy) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-5xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('common.retry')}
                </button>
                <Link
                  to="/proxies/list"
                  className="px-6 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.back')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/proxies/list"
              className="text-brand-accent hover:text-brand-accent/80 transition-colors font-body"
            >
              ← {t('common.back')}
            </Link>
            <span
              className={`px-3 py-1 rounded-full text-sm font-body ${
                proxy.status === 'active'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : proxy.status === 'expired'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                    : 'bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50'
              }`}
            >
              {t(`proxies.status.${proxy.status}`)}
            </span>
          </div>

          {/* Connection Details */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
              {proxy.serverId === 4 ? 'Rotating Mobile Proxy 🔥' : t('proxies.details.connectionDetails')}
            </h2>

            <div className="border border-neutral-dark/70 rounded-xl divide-y divide-neutral-dark/70 overflow-hidden">
              {/* IP Address */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.ipAddress')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.ipAddress}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(proxy.ipAddress)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                  aria-label={t('common.copy')}
                  title={t('common.copy')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                  </svg>
                </button>
              </div>

              {/* HTTP Port */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.port')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.port}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(proxy.port.toString())}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                  aria-label={t('common.copy')}
                  title={t('common.copy')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                  </svg>
                </button>
              </div>

              {/* SOCKS Port (shown when available) */}
              {proxy.port_socks && (
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="flex-1">
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.portSocks') || 'SOCKS Port'}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {proxy.port_socks}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(proxy.port_socks!.toString())}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Username */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.username')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.pkgId && isInjectionPkg(proxy.pkgId) ? '—' : proxy.username}
                  </p>
                </div>
                {!(proxy.pkgId && isInjectionPkg(proxy.pkgId)) && (
                  <button
                    type="button"
                    onClick={() => handleCopy(proxy.username)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Password (hidden for Injection packages) */}
              {proxy.pkgId && isInjectionPkg(proxy.pkgId) ? (
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="flex-1">
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.password')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">—</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="flex-1">
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.password')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {showPassword ? proxy.password : '••••••••'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-neutral-dark/70 text-neutral-light hover:bg-neutral-dark transition-colors"
                      aria-label={showPassword ? t('proxies.details.hide') : t('proxies.details.show')}
                      title={showPassword ? t('proxies.details.hide') : t('proxies.details.show')}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-10-7 1.028-3.085 3.983-6.364 7.405-6.9M9.88 9.88A3 3 0 1114.12 14.12M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopy(proxy.password)}
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                      aria-label={t('common.copy')}
                      title={t('common.copy')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Connection String */}
              <div className="p-4 bg-brand-accent/10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-neutral-medium font-body">
                    {t('proxies.details.connectionString')}
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyConnectionString}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent text-brand-primary hover:shadow-lg hover:shadow-brand-accent/40 transition-all"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm font-mono text-neutral-light break-all">
                  {proxy.pkgId && isInjectionPkg(proxy.pkgId)
                    ? `${proxy.ipAddress}:${proxy.port}`
                    : `${proxy.username}:${proxy.password}@${proxy.ipAddress}:${proxy.port}`}
                </p>
              </div>
            </div>
          </div>

          {/* Proxy Information */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
              {t('proxies.details.information')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.location')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.location}
                  </p>
                </div>
                {proxy.city && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.city')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {proxy.city}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.type')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.type.toUpperCase()}
                  </p>
                </div>
                {proxy.pkgId && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.packageName') || 'Package'}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {pkgDisplayNames[Number(proxy.pkgId)] || `${t('proxies.wizard.server4.package')} ${proxy.pkgId}`}
                      <span className="text-sm text-neutral-medium ml-2">({getPkgTypeName(Number(proxy.pkgId))})</span>
                    </p>
                  </div>
                )}
                {proxy.providerName && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.providerName') || 'Provider'}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {proxy.providerName}
                    </p>
                  </div>
                )}
                {proxy.rotationTime != null && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.rotationTime')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {proxy.rotationTime} min
                    </p>
                  </div>
                )}
                {proxy.serverId === 4 && proxy.pkgId && isInjectionPkg(proxy.pkgId) && (
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">{t('proxies.details.activeIps') || 'Active IPs'}</p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {Array.isArray(proxy.allowedIps) ? proxy.allowedIps.length : 0} / 3
                  </p>
                </div>
              )}
              {proxy.serverId === 4 && fullDetails5gData?.data && (
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">{t('proxies.details.bandwidth') || 'Bandwidth'}</p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {((fullDetails5gData.data.today_bandwidth_kbytes ?? 0) / (1024 * 1024)).toFixed(2)} GB
                  </p>
                  <p className="text-xs text-neutral-medium font-body mt-1">
                    ℹ️ {t('proxies.details.bandwidthDailyLimit') || 'Daily limit: 15 GB'}
                  </p>
                </div>
              )}
              {proxy.serverId === 4 && fullDetails5gData?.data && (
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">{t('proxies.details.status')}</p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {fullDetails5gData.data.status || proxy.status}
                  </p>
                </div>
              )}
              {proxy.serverId === 4 && fullDetails5gData?.data?.suspend_reason && (
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">{t('proxies.details.suspendReason') || 'Suspend Reason'}</p>
                  <p className="text-lg font-heading font-semibold text-danger">
                    {(() => {
                      const reason = fullDetails5gData.data!.suspend_reason!;
                      const messages: Record<string, string> = {
                        GB_LIMIT: t('proxies.details.suspendGbLimit') || 'Suspended due to daily bandwidth limit exceeded',
                        IP_LIMIT: t('proxies.details.suspendIpLimit') || 'Suspended due to exceeded allowed IPs',
                        ADMIN: t('proxies.details.suspendAdmin') || 'Suspended by administration, contact support',
                      };
                      return messages[reason] || reason;
                    })()}
                  </p>
                </div>
              )}
              {proxy.serverId === 4 && fullDetails5gData?.data?.rotation_time != null && !proxy.rotationTime && (
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">{t('proxies.details.rotationTime')}</p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {fullDetails5gData.data.rotation_time} min
                  </p>
                </div>
              )}
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.expiresAt')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {formatDate(proxy.expiresAt, 'PP h:mm a', currentLanguage)}
                  </p>
                  <p className="text-xs text-brand-accent font-body mt-1">
                    {formatRemainingTime(proxy.expiresAt, t)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.purchasedAt')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {formatDate(proxy.purchasedAt, 'PP h:mm a', currentLanguage)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.price')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-brand-accent">
                    ${Number(proxy.price).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {proxy.status !== 'expired' && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
              <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
                {t('proxies.details.actions')}
              </h2>

              <div className="space-y-4">
                {proxy.serverId === 1 && (
                  <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                    <label className="text-sm text-neutral-medium font-body">
                      {t('proxies.details.renewPeriod')} ({t('proxies.wizard.months')})
                    </label>
                    <select
                      value={selectedRenewMonths}
                      onChange={(e) => setSelectedRenewMonths(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    >
                      {[1, 2, 3, 6, 12].map((months) => (
                        <option key={months} value={months}>{months} {t(months === 1 ? 'proxies.wizard.month' : 'proxies.wizard.months')}</option>
                      ))}
                    </select>
                    {extendPriceData?.data && (
                      <p className="text-sm text-brand-accent font-heading font-semibold">
                        {t('proxies.details.price')}: ${extendPriceData.data.price.toFixed(2)}
                      </p>
                    )}
                    {isFetchingPrice && (
                      <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        requestConfirm(
                          t('proxies.details.renewNow'),
                          `${t('proxies.details.confirmRenew')} (${selectedRenewMonths} ${t(selectedRenewMonths === 1 ? 'proxies.wizard.month' : 'proxies.wizard.months')}${extendPriceData?.data ? ` — $${extendPriceData.data.price.toFixed(2)}` : ''})`,
                          () => { void handleRenew(); }
                        );
                      }}
                      disabled={isRenewing || isFetchingPrice}
                      className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                    </button>
                  </div>
                )}

                {proxy.serverId === 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      requestConfirm(
                        t('proxies.details.renewNow'),
                        t('proxies.details.confirmRenew'),
                        () => { void handleRenew(); }
                      );
                    }}
                    disabled={isRenewing}
                    className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                  </button>
                )}

                {proxy.serverId === 3 && (
                  <>
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                      <label className="text-sm text-neutral-medium font-body">
                        {t('proxies.details.renewPeriod')}
                      </label>
                      <select
                        value={selectedRenewDays}
                        onChange={(e) => setSelectedRenewDays(Number(e.target.value))}
                        className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                      >
                        {(server3Options?.renewalPeriods ?? []).map((days) => (
                          <option key={days} value={days}>{days} {t('proxies.wizard.days')}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.renewNow'),
                            t('proxies.details.confirmRenew'),
                            () => { void handleRenew(); }
                          );
                        }}
                        disabled={isRenewing || selectedRenewDays === ''}
                        className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                      </button>
                    </div>

                    {(server3Options?.options.countries.length ?? 0) > 0 && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <label className="text-sm text-neutral-medium font-body">{t('proxies.details.updateCountry')}</label>
                        <select
                          value={selectedCountryCode}
                          onChange={(e) => setSelectedCountryCode(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          disabled={isLockActive(lockDates?.countryUpdateLockedUntil)}
                        >
                          {(server3Options?.options.countries ?? []).map((country) => (
                            <option key={country.code} value={country.code}>{country.name}</option>
                          ))}
                        </select>
                        {isLockActive(lockDates?.countryUpdateLockedUntil) && (
                          <p className="text-xs text-amber-400">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.countryUpdateLockedUntil) })}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            requestConfirm(
                              t('proxies.details.updateCountry'),
                              t('proxies.details.confirmUpdate'),
                              () => { void handleServer3Action('change_country', selectedCountryCode); }
                            );
                          }}
                          disabled={isUpdatingServer3 || !selectedCountryCode || isLockActive(lockDates?.countryUpdateLockedUntil)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                        >
                          {t('proxies.details.update')}
                        </button>
                      </div>
                    )}

                    {(server3Options?.options.states.length ?? 0) > 0 && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <label className="text-sm text-neutral-medium font-body">{t('proxies.details.updateRegion')}</label>
                        <select
                          value={selectedRegionCode}
                          onChange={(e) => setSelectedRegionCode(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          disabled={isLockActive(lockDates?.regionUpdateLockedUntil)}
                        >
                          {(server3Options?.options.states ?? []).map((state) => (
                            <option key={state.code} value={state.code}>{state.name}</option>
                          ))}
                        </select>
                        {isLockActive(lockDates?.regionUpdateLockedUntil) && (
                          <p className="text-xs text-amber-400">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.regionUpdateLockedUntil) })}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            requestConfirm(
                              t('proxies.details.updateRegion'),
                              t('proxies.details.confirmUpdate'),
                              () => { void handleServer3Action('change_region', selectedRegionCode); }
                            );
                          }}
                          disabled={isUpdatingServer3 || !selectedRegionCode || isLockActive(lockDates?.regionUpdateLockedUntil)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                        >
                          {t('proxies.details.update')}
                        </button>
                      </div>
                    )}

                    {(server3Options?.options.carriers.length ?? 0) > 0 && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <label className="text-sm text-neutral-medium font-body">{t('proxies.details.updateCarrier')}</label>
                        <select
                          value={selectedCarrierCode}
                          onChange={(e) => setSelectedCarrierCode(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          disabled={isLockActive(lockDates?.carrierUpdateLockedUntil)}
                        >
                          {(server3Options?.options.carriers ?? []).map((carrier) => (
                            <option key={carrier.code} value={carrier.code}>{carrier.name}</option>
                          ))}
                        </select>
                        {isLockActive(lockDates?.carrierUpdateLockedUntil) && (
                          <p className="text-xs text-amber-400">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.carrierUpdateLockedUntil) })}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            requestConfirm(
                              t('proxies.details.updateCarrier'),
                              t('proxies.details.confirmUpdate'),
                              () => { void handleServer3Action('change_carrier', selectedCarrierCode); }
                            );
                          }}
                          disabled={isUpdatingServer3 || !selectedCarrierCode || isLockActive(lockDates?.carrierUpdateLockedUntil)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                        >
                          {t('proxies.details.update')}
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.updateProxyTypeOnce'),
                            t('proxies.details.confirmUpdate'),
                            () => { void handleServer3Action('change_proxy_type'); }
                          );
                        }}
                        disabled={isUpdatingServer3 || Boolean(lockDates?.proxyTypeIsLocked)}
                        className="px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                      >
                        {t('proxies.details.updateProxyTypeOnce')}
                        {Boolean(lockDates?.proxyTypeIsLocked) && (
                          <span className="block text-xs text-amber-400 mt-1">
                            {t('proxies.details.locked')}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.toggleRotation'),
                            t('proxies.details.confirmUpdate'),
                            () => { void handleServer3Action('toggle_rotation'); }
                          );
                        }}
                        disabled={isUpdatingServer3 || isLockActive(lockDates?.clientIpUpdateLockedUntil)}
                        className="px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                      >
                        {t('proxies.details.toggleRotation')}
                        {isLockActive(lockDates?.clientIpUpdateLockedUntil) && (
                          <span className="block text-xs text-amber-400 mt-1">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.clientIpUpdateLockedUntil) })}
                          </span>
                        )}
                      </button>
                    </div>
                  </>
                )}

                  {proxy.serverId === 4 && (
                  <>
                    {/* Renew */}
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                          <label className="text-sm text-neutral-medium font-body">
                            {t('proxies.details.renewPeriod')}
                          </label>
                          <select
                            value={selectedRenewDays5g}
                            onChange={(e) => setSelectedRenewDays5g(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          >
                            {(() => {
                              const pkgPrices = (server4Options?.options?.prices || []).filter(p => p.pkgId === proxy.pkgId && Number(p.price) > 0);
                              const hours = [...new Set(pkgPrices.filter(p => p.hours > 0).map(p => p.hours))].sort((a, b) => a - b);
                              const days = [...new Set(pkgPrices.filter(p => p.days > 0).map(p => p.days))].sort((a, b) => a - b);
                              if (hours.length === 0 && days.length === 0) {
                                return [...[2, 3, 12].map((h) => (
                                  <option key={`${h}h`} value={`${h}h`}>{h}h</option>
                                )), ...[1, 2, 7, 15, 30].map((d) => (
                                  <option key={`${d}d`} value={`${d}d`}>{d}d</option>
                                ))];
                              }
                              return [
                                ...hours.map((h) => (
                                  <option key={`${h}h`} value={`${h}h`}>{h}h</option>
                                )),
                                ...days.map((d) => (
                                  <option key={`${d}d`} value={`${d}d`}>{d}d</option>
                                )),
                              ];
                            })()}
                          </select>
                      {isFetchingPrice5g && (
                        <div className="text-sm text-neutral-medium font-body">{t('common.loading')}...</div>
                      )}
                      {extendPrice5gData?.data && (
                        <div className="text-sm text-neutral-light font-body">
                          {t('proxies.details.renewPrice') || 'Price'}: ${Number(extendPrice5gData.data.price).toFixed(2)}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const priceText = extendPrice5gData?.data
                            ? `\n💰 ${t('proxies.details.renewPrice') || 'Price'}: $${Number(extendPrice5gData.data.price).toFixed(2)}`
                            : '';
                          requestConfirm(
                            t('proxies.details.renewNow'),
                            `${t('proxies.details.confirmRenew')} (${selectedRenewDays5g} ${t('proxies.wizard.days')})${priceText}`,
                            () => { void handleRenew(); }
                          );
                        }}
                        disabled={isRenewing || isFetchingPrice5g}
                        className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                      </button>
                    </div>

                    {/* IP Management (Injection) */}
                    {proxy?.pkgId && isInjectionPkg(Number(proxy.pkgId)) && (
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                      <h3 className="text-sm text-neutral-medium font-body font-semibold">
                        {t('proxies.details.ipManagement') || '📡 IP Management'}
                      </h3>
                      <div className="rounded-lg border border-neutral-dark bg-brand-primary/20 p-3">
                        <p className="text-xs text-amber-400 font-body">{t('proxies.details.ipLimit') || 'Max 3 IPs allowed'}</p>
                      </div>
                      {(Array.isArray(proxy.allowedIps) && proxy.allowedIps.length > 0) ? (
                        <div className="space-y-2">
                          {proxy.allowedIps.map((ip, idx) => (
                            <div key={idx} className="flex items-center justify-between px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/30">
                              <span className="text-sm font-mono text-neutral-light">IP {idx + 1}: {ip}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  requestConfirm(
                                    t('proxies.details.deleteIp') || 'Delete IP',
                                    t('proxies.details.confirmDeleteIp', { ip }) || `Remove ${ip}?`,
                                    () => { void handleRemoveIpFromProxy5g(idx); }
                                  );
                                }}
                                disabled={isRemovingIp5g}
                                className="px-3 py-1 text-xs rounded-lg border border-danger/50 text-danger hover:bg-danger/10 transition-all disabled:opacity-60"
                              >
                                {isRemovingIp5g ? '...' : (t('proxies.details.deleteIpBtn') || 'Delete')}
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-neutral-medium font-body">{t('proxies.details.noIps') || 'No IPs added yet'}</p>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={addIpAddress}
                          onChange={(e) => setAddIpAddress(e.target.value)}
                          placeholder={t('proxies.details.enterIpPlaceholder') || 'Enter IP address to add'}
                          className="flex-1 px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                        />
                        <button
                          type="button"
                          onClick={() => { void handleAddIpToProxy5g(); }}
                          disabled={isAddingIp5g || !addIpAddress.trim() || (Array.isArray(proxy.allowedIps) && proxy.allowedIps.length >= 3)}
                          className="px-4 py-2 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold text-sm hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isAddingIp5g ? '...' : (t('proxies.details.addIpBtn') || 'Add IP')}
                        </button>
                      </div>
                    </div>
                    )}

                    {/* Change Password (User/Pass) */}
                    {proxy?.pkgId && !isInjectionPkg(Number(proxy.pkgId)) && (
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                      <h3 className="text-sm text-neutral-medium font-body font-semibold">
                        {t('proxies.details.changePassword') || '🔑 Change Password'}
                      </h3>
                      <div>
                        <label className="text-xs text-neutral-medium font-body">{t('proxies.details.newPassword') || 'New Password'}</label>
                        <input
                          type="text"
                          value={modifyPassword}
                          onChange={(e) => setModifyPassword(e.target.value)}
                          placeholder={t('proxies.details.newPassword') || 'New password'}
                          className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent mt-1"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.changePassword') || 'Change Password',
                            t('proxies.details.confirmUpdate'),
                            () => { void handleModifyProxy5g('password'); }
                          );
                        }}
                        disabled={isModifying5g || !modifyPassword.trim()}
                        className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                      >
                        {isModifying5g ? t('common.processing') : t('proxies.details.update')}
                      </button>
                    </div>
                    )}

                    {/* Change Protocol (User/Pass) */}
                    {proxy?.pkgId && !isInjectionPkg(Number(proxy.pkgId)) && (
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                      <h3 className="text-sm text-neutral-medium font-body font-semibold">
                        {t('proxies.details.changeProtocol') || '🌐 Change Protocol'}
                      </h3>
                      {protocolCooldownRemaining > 0 ? (
                        <p className="text-xs text-amber-400 font-body">
                          {t('proxies.details.protocolCooldown', { minutes: protocolCooldownRemaining })}
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              requestConfirm(
                                t('proxies.details.changeProtocol') || 'Change Protocol',
                                `${t('proxies.details.confirmUpdate')}\nProtocol → HTTP`,
                                () => { void handleModifyProxy5g('protocol', 'http'); }
                              );
                            }}
                            disabled={isModifying5g || modifyProtocol === 'http' || protocolCooldownRemaining > 0}
                            className={`px-4 py-3 rounded-xl border font-body font-semibold transition-all ${
                              modifyProtocol === 'http'
                                ? 'border-green-500 bg-green-500/20 text-green-400'
                                : 'border-neutral-dark text-neutral-light hover:border-brand-accent/50'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            🌐 HTTP
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              requestConfirm(
                                t('proxies.details.changeProtocol') || 'Change Protocol',
                                `${t('proxies.details.confirmUpdate')}\nProtocol → SOCKS`,
                                () => { void handleModifyProxy5g('protocol', 'socks'); }
                              );
                            }}
                            disabled={isModifying5g || modifyProtocol === 'socks' || protocolCooldownRemaining > 0}
                            className={`px-4 py-3 rounded-xl border font-body font-semibold transition-all ${
                              modifyProtocol === 'socks'
                                ? 'border-green-500 bg-green-500/20 text-green-400'
                                : 'border-neutral-dark text-neutral-light hover:border-brand-accent/50'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                          >
                            🔐 SOCKS
                          </button>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Change Package */}
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                      <h3 className="text-sm text-neutral-medium font-body font-semibold">
                        {t('proxies.details.changePackage') || 'Change Package'}
                      </h3>
                      <p className="text-xs text-neutral-medium font-body">
                        {t('proxies.details.currentPackage') || 'Current'}: {pkgDisplayNames[Number(currentPkg)] || server4Packages.find(p => Number(p.id) === Number(currentPkg))?.name || `${t('proxies.wizard.server4.package')} ${currentPkg}`}
                        ({getPkgTypeName(currentPkg)})
                      </p>

                      {pkgChangeStep === 'idle' && (
                        <>
                          <select
                            value={changePkgId}
                            onChange={(e) => {
                              const val = e.target.value ? Number(e.target.value) : '';
                              setChangePkgId(val);
                              setPkgChangeProtocol(val ? (isInjectionPkg(Number(val)) ? 'http' : proxy?.type || 'http') : 'http');
                              setPkgChangeIpAddr('');
                              setPkgChangeUsername('');
                              setPkgChangePassword('');
                              setPkgChangeUsernameToCheck('');
                              if (val) setPkgChangeStep('country');
                            }}
                            className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          >
                            <option value="">{t('proxies.wizard.server4.selectPackage') || 'Select package'}</option>
                            {allowedPackages.map((pkg) => (
                              <option key={pkg.id} value={Number(pkg.id)}>{getPkgDisplayName(pkg)}</option>
                            ))}
                            {allowedPackages.length === 0 && (
                              <option value="" disabled>{t('proxies.details.noSwitchAvailable') || 'No switch available for this package'}</option>
                            )}
                          </select>
                        </>
                      )}

                      {pkgChangeStep === 'country' && (
                        <div className="space-y-3">
                          <h4 className="text-xs text-brand-accent font-body font-semibold">
                            1/4 — {t('proxies.details.selectNewCountry') || 'Select new country'}
                          </h4>
                          <input
                            type="text"
                            value={pkgChangeCountrySearch}
                            onChange={(e) => setPkgChangeCountrySearch(e.target.value)}
                            placeholder={t('proxies.wizard.server4.searchCountry') || 'Search country...'}
                            className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          />
                          <ul className="max-h-48 overflow-y-auto divide-y divide-neutral-dark/50">
                            {(pkgChangeCountrySearch.trim()
                              ? server4Countries.filter(c => c.name.toLowerCase().includes(pkgChangeCountrySearch.toLowerCase()))
                              : server4Countries
                            ).map((country) => (
                              <li key={country.id}>
                                <button
                                  type="button"
                                  onClick={() => { setPkgChangeCountry(String(country.id)); setPkgChangeStep('city'); }}
                                  className="w-full px-3 py-2 text-left text-sm text-neutral-light hover:bg-brand-accent/20 transition-colors"
                                >
                                  {country.name} {getFlagEmoji((country as Server4Country & { code?: string }).code)}
                                </button>
                              </li>
                            ))}
                          </ul>
                          <button type="button" onClick={handleChangePkgBack} className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all">
                            {t('common.back')}
                          </button>
                        </div>
                      )}

                      {pkgChangeStep === 'city' && (
                        <div className="space-y-3">
                          <h4 className="text-xs text-brand-accent font-body font-semibold">
                            2/4 — {t('proxies.details.selectNewCity') || 'Select new city'}
                          </h4>
                          {pkgChangeCities.length === 0 ? (
                            <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={pkgChangeCitySearch}
                                onChange={(e) => setPkgChangeCitySearch(e.target.value)}
                                placeholder={t('proxies.wizard.server4.searchCity') || 'Search city...'}
                                className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                              />
                              <ul className="max-h-48 overflow-y-auto divide-y divide-neutral-dark/50">
                                {(pkgChangeCitySearch.trim()
                                  ? pkgChangeCities.filter(c => c.name.toLowerCase().includes(pkgChangeCitySearch.toLowerCase()))
                                  : pkgChangeCities
                                ).map((city) => (
                                  <li key={city.id}>
                                    <button
                                      type="button"
                                      onClick={() => { setPkgChangeCity(String(city.id)); setPkgChangeStep('serviceProvider'); }}
                                      className="w-full px-3 py-2 text-left text-sm text-neutral-light hover:bg-brand-accent/20 transition-colors"
                                    >
                                      {city.name}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                          <button type="button" onClick={handleChangePkgBack} className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all">
                            {t('common.back')}
                          </button>
                        </div>
                      )}

                      {pkgChangeStep === 'serviceProvider' && (
                        <div className="space-y-3">
                          <h4 className="text-xs text-brand-accent font-body font-semibold">
                            3/4 — {t('proxies.wizard.server4.selectServiceProvider') || 'Select Service Provider'}
                          </h4>
                          {loadingPkgChangeProviders ? (
                            <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                          ) : pkgChangeProviders.length === 0 ? (
                            <p className="text-sm text-neutral-medium">{t('proxies.wizard.server4.noParentProxies') || 'No providers available'}</p>
                          ) : (
                            <ul className="max-h-48 overflow-y-auto divide-y divide-neutral-dark/50">
                              {pkgChangeProviders.map((provider) => (
                                <li key={provider.id}>
                                  <button
                                    type="button"
                                    onClick={() => { setPkgChangeServiceProvider(String(provider.id)); setPkgChangeParentProxy(''); setPkgChangeStep('parentProxy'); }}
                                    className="w-full px-3 py-2 text-left text-sm text-neutral-light hover:bg-brand-accent/20 transition-colors"
                                  >
                                    {provider.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <button type="button" onClick={handleChangePkgBack} className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all">
                            {t('common.back')}
                          </button>
                        </div>
                      )}

                      {pkgChangeStep === 'parentProxy' && (
                        <div className="space-y-3">
                          <h4 className="text-xs text-brand-accent font-body font-semibold">
                            4/4 — {t('proxies.details.selectNewParentProxy') || 'Select new parent proxy'}
                          </h4>
                          {loadingPkgChangeProxies ? (
                            <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                          ) : pkgChangeParentProxies.length === 0 ? (
                            <p className="text-sm text-neutral-medium">{t('proxies.wizard.server4.noParentProxies') || 'No parent proxies available'}</p>
                          ) : (
                            <div className="max-h-48 overflow-y-auto space-y-2">
                              {pkgChangeParentProxies.map((pp) => (
                                <button
                                  key={pp.id}
                                  type="button"
                                  onClick={() => {
                                    setPkgChangeParentProxy(String(pp.id));
                                    setPkgChangeStep('credentials');
                                  }}
                                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                                    String(pp.id) === pkgChangeParentProxy
                                      ? 'border-brand-accent bg-brand-accent/20'
                                      : 'border-neutral-dark bg-neutral-dark hover:border-brand-accent/50'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-neutral-light font-heading font-semibold text-sm">{pp.ip || '-'}</span>
                                    {pp.technology && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">{pp.technology}</span>}
                                  </div>
                                  <div className="space-y-0.5 text-xs font-body">
                                    <div className="flex justify-between"><span className="text-neutral-medium">HTTP Port:</span><span className="text-neutral-light font-mono">{pp.httpPort ?? '-'}</span></div>
                                    <div className="flex justify-between"><span className="text-neutral-medium">SOCKS Port:</span><span className="text-neutral-light font-mono">{pp.socksPort ?? '-'}</span></div>
                                    {pp.rotationTime && <div className="flex justify-between"><span className="text-neutral-medium">Rotation:</span><span className="text-neutral-light">{pp.rotationTime}</span></div>}
                                    {pp.provider && <div className="flex justify-between"><span className="text-neutral-medium">Provider:</span><span className="text-neutral-light">{pp.provider}</span></div>}
                                    {pp.city && <div className="flex justify-between"><span className="text-neutral-medium">City:</span><span className="text-neutral-light">{pp.city}</span></div>}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          <button type="button" onClick={handleChangePkgBack} className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all">
                            {t('common.back')}
                          </button>
                        </div>
                      )}

                      {pkgChangeStep === 'credentials' && (
                        <div className="space-y-3">
                          <h4 className="text-xs text-brand-accent font-body font-semibold">
                            {t('proxies.details.credentials') || 'Credentials'}
                          </h4>

                          {isInjectionPkg(Number(changePkgId)) ? (
                            <div>
                              <label className="text-xs text-neutral-medium font-body">
                                {t('proxies.details.ipAddrForInjection') || 'IP Address for Injection'} *
                              </label>
                              <input
                                type="text"
                                value={pkgChangeIpAddr}
                                onChange={(e) => setPkgChangeIpAddr(e.target.value)}
                                placeholder={t('proxies.details.ipAddrPlaceholder') || 'Enter IP address (e.g. 1.2.3.4)'}
                                className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent mt-1"
                              />
                              <p className="text-xs text-amber-400 mt-1">
                                {t('proxies.details.ipAddrHint') || 'Protocol will be set to HTTP for Injection packages'}
                              </p>
                            </div>
                          ) : (
                            <>
                              <div>
                                <label className="text-xs text-neutral-medium font-body">{t('proxies.wizard.server4.protocol') || 'Protocol'}</label>
                                <select
                                  value={pkgChangeProtocol}
                                  onChange={(e) => setPkgChangeProtocol(e.target.value)}
                                  className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent mt-1"
                                >
                                  <option value="http">HTTP</option>
                                  <option value="socks">SOCKS</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-xs text-neutral-medium font-body">
                                  {t('proxies.details.newUsername') || 'New username'} *
                                </label>
                                <div className="flex gap-2 mt-1">
                                  <input
                                    type="text"
                                    value={pkgChangeUsername}
                                    onChange={(e) => { setPkgChangeUsername(e.target.value); setPkgChangeUsernameToCheck(''); }}
                                    placeholder={t('proxies.wizard.server4.usernamePlaceholder') || 'Choose a username'}
                                    className="flex-1 px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => { if (pkgChangeUsername.trim()) setPkgChangeUsernameToCheck(pkgChangeUsername.trim()); }}
                                    disabled={!pkgChangeUsername.trim() || pkgChangeCheckingUsername}
                                    className="px-3 py-2 rounded-lg bg-brand-accent/20 text-brand-accent text-sm font-body hover:bg-brand-accent/30 transition-all disabled:opacity-50"
                                  >
                                    {pkgChangeCheckingUsername ? '...' : (t('proxies.details.checkUsername') || 'Check')}
                                  </button>
                                </div>
                                {pkgChangeUsernameToCheck && (
                                  <p className={`text-xs mt-1 ${pkgChangeUsernameAvailable ? 'text-green-400' : 'text-red-400'}`}>
                                    {pkgChangeCheckingUsername
                                      ? `${t('common.loading')}...`
                                      : pkgChangeUsernameAvailable
                                        ? (t('proxies.wizard.server4.usernameAvailable') || 'Username available')
                                        : (t('proxies.wizard.server4.usernameTaken') || 'Username taken')}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label className="text-xs text-neutral-medium font-body">
                                  {t('proxies.details.newPassword') || 'New password'} *
                                </label>
                                <input
                                  type="text"
                                  value={pkgChangePassword}
                                  onChange={(e) => setPkgChangePassword(e.target.value)}
                                  placeholder={t('proxies.wizard.server4.passwordPlaceholder') || 'Enter password'}
                                  className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent mt-1"
                                />
                              </div>
                            </>
                          )}

                          <button type="button" onClick={handleChangePkgBack} className="w-full px-4 py-3 rounded-xl bg-neutral-dark text-neutral-light font-body hover:bg-neutral-dark/80 transition-all">
                            {t('common.back')}
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              const countryName = server4Countries.find(c => String(c.id) === pkgChangeCountry)?.name || pkgChangeCountry;
                              const cityName = pkgChangeCities.find(c => String(c.id) === pkgChangeCity)?.name || pkgChangeCity;
                              let msg = `${pkgDisplayNames[Number(changePkgId)] || `Package ${changePkgId}`}\n${countryName} — ${cityName}`;
                              if (isInjectionPkg(Number(changePkgId))) {
                                if (pkgChangeIpAddr.trim()) msg += `\nIP: ${pkgChangeIpAddr.trim()}`;
                              } else {
                                msg += `\nProtocol: ${(pkgChangeProtocol || 'http').toUpperCase()}`;
                                if (pkgChangeUsername.trim()) msg += `\nUsername: ${pkgChangeUsername.trim()}`;
                              }
                              requestConfirm(
                                t('proxies.details.changePackageConfirm') || 'Confirm Package Change',
                                msg,
                                () => { void handleChangeProxy5gPackage(); }
                              );
                            }}
                            disabled={
                              isChangingPkg5g || changePkgId === '' || !pkgChangeParentProxy ||
                              (isInjectionPkg(Number(changePkgId)) && !pkgChangeIpAddr.trim()) ||
                              (!isInjectionPkg(Number(changePkgId)) && (!pkgChangeUsername.trim() || !pkgChangePassword.trim() || !pkgChangeUsernameAvailable))
                            }
                            className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                          >
                            {isChangingPkg5g ? t('common.processing') : t('proxies.details.update')}
                          </button>
                        </div>
                      )}

                      {pkgChangeStep === 'idle' && allowedPackages.length === 0 && (
                        <p className="text-xs text-neutral-medium font-body">
                          {t('proxies.details.noSwitchAvailable') || 'No switch available for this package'}
                        </p>
                      )}
                    </div>

                    {/* Location Change */}
                    {locationStep === 'idle' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (proxy?.pkgId && isInjectionPkg(proxy.pkgId)) {
                            setLocationProtocol('http');
                            setLocationStep('country');
                          } else {
                            setLocationProtocol(proxy?.type?.toLowerCase() || 'http');
                            setLocationStep('protocol');
                          }
                        }}
                        className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all text-neutral-light font-body"
                      >
                        {t('proxies.details.locationChange') || '🌍 Change Location'}
                      </button>
                    )}

                    {locationStep === 'protocol' && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <h3 className="text-sm text-brand-accent font-body font-semibold">
                          {t('proxies.details.selectProtocolFirst') || 'Select Protocol'}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => { setLocationProtocol('http'); setLocationStep('country'); }}
                            className={`px-4 py-3 rounded-xl border font-body font-semibold transition-all ${
                              locationProtocol === 'http'
                                ? 'border-brand-accent bg-brand-accent/20 text-brand-accent'
                                : 'border-neutral-dark text-neutral-light hover:border-brand-accent/50'
                            }`}
                          >
                            🌐 HTTP
                          </button>
                          <button
                            type="button"
                            onClick={() => { setLocationProtocol('socks'); setLocationStep('country'); }}
                            className={`px-4 py-3 rounded-xl border font-body font-semibold transition-all ${
                              locationProtocol === 'socks'
                                ? 'border-brand-accent bg-brand-accent/20 text-brand-accent'
                                : 'border-neutral-dark text-neutral-light hover:border-brand-accent/50'
                            }`}
                          >
                            🔐 SOCKS
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={handleLocationBack}
                          className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all"
                        >
                          {t('common.back')}
                        </button>
                      </div>
                    )}

                    {locationStep === 'country' && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <h3 className="text-sm text-neutral-medium font-body font-semibold">
                          {t('proxies.details.selectNewCountry') || 'Select new country'}
                        </h3>
                        <input
                          type="text"
                          value={locationCountrySearch}
                          onChange={(e) => setLocationCountrySearch(e.target.value)}
                          placeholder={t('proxies.wizard.server4.searchCountry') || 'Search country...'}
                          className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                        />
                        <ul className="max-h-48 overflow-y-auto divide-y divide-neutral-dark/50">
                          {(locationCountrySearch.trim()
                            ? locationCountries.filter(c => c.name.toLowerCase().includes(locationCountrySearch.toLowerCase()))
                            : locationCountries
                          ).map((country) => (
                            <li key={country.id}>
                              <button
                                type="button"
                                onClick={() => { setLocationCountry(String(country.id)); setLocationStep('city'); }}
                                className="w-full px-3 py-2 text-left text-sm text-neutral-light hover:bg-brand-accent/20 transition-colors"
                              >
                                {country.name} {getFlagEmoji((country as Server4Country & { code?: string }).code)}
                              </button>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={handleLocationBack}
                          className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all"
                        >
                          {t('common.back')}
                        </button>
                      </div>
                    )}

                    {locationStep === 'city' && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <h3 className="text-sm text-neutral-medium font-body font-semibold">
                          {t('proxies.details.selectNewCity') || 'Select new city'}
                        </h3>
                        {locationCities.length === 0 ? (
                          <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                        ) : (
                          <>
                            <input
                              type="text"
                              value={locationCitySearch}
                              onChange={(e) => setLocationCitySearch(e.target.value)}
                              placeholder={t('proxies.wizard.server4.searchCity') || 'Search city...'}
                              className="w-full px-3 py-2 rounded-lg border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent"
                            />
                            <ul className="max-h-48 overflow-y-auto divide-y divide-neutral-dark/50">
                              {(locationCitySearch.trim()
                                ? locationCities.filter(c => c.name.toLowerCase().includes(locationCitySearch.toLowerCase()))
                                : locationCities
                              ).map((city) => (
                                <li key={city.id}>
                                  <button
                                    type="button"
                                    onClick={() => { setLocationCity(String(city.id)); setLocationStep('serviceProvider'); }}
                                    className="w-full px-3 py-2 text-left text-sm text-neutral-light hover:bg-brand-accent/20 transition-colors"
                                  >
                                    {city.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={handleLocationBack}
                          className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all"
                        >
                          {t('common.back')}
                        </button>
                      </div>
                    )}

                    {locationStep === 'serviceProvider' && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <h3 className="text-sm text-neutral-medium font-body font-semibold">
                          {t('proxies.wizard.server4.selectServiceProvider') || 'Select Service Provider'}
                        </h3>
                        {loadingLocationProviders ? (
                          <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                        ) : locationProviders.length === 0 ? (
                          <p className="text-sm text-neutral-medium">{t('proxies.wizard.server4.noParentProxies') || 'No providers available'}</p>
                        ) : (
                          <ul className="max-h-48 overflow-y-auto divide-y divide-neutral-dark/50">
                            {locationProviders.map((provider) => (
                              <li key={provider.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setLocationServiceProvider(String(provider.id));
                                    setLocationParentProxy('');
                                    setLocationStep('parentProxy');
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-neutral-light hover:bg-brand-accent/20 transition-colors"
                                >
                                  {provider.name}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <button
                          type="button"
                          onClick={() => { setLocationServiceProvider(''); setLocationStep('city'); }}
                          className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all"
                        >
                          {t('common.back')}
                        </button>
                      </div>
                    )}

                    {locationStep === 'parentProxy' && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <h3 className="text-sm text-neutral-medium font-body font-semibold">
                          {t('proxies.details.selectNewParentProxy') || 'Select new parent proxy'}
                        </h3>
                        {loadingLocationProxies ? (
                          <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                        ) : locationParentProxies.length === 0 ? (
                          <p className="text-sm text-neutral-medium">{t('proxies.wizard.server4.noParentProxies') || 'No parent proxies available'}</p>
                        ) : (
                          <div className="max-h-48 overflow-y-auto space-y-2">
                            {locationParentProxies.map((pp) => (
                              <button
                                key={pp.id}
                                type="button"
                                onClick={() => {
                                  setLocationParentProxy(String(pp.id));
                                  const countryName = locationCountries.find(c => String(c.id) === locationCountry)?.name || locationCountry;
                                  const cityName = locationCities.find(c => String(c.id) === locationCity)?.name || locationCity;
                                  requestConfirm(
                                    t('proxies.details.confirmLocationChange') || 'Confirm Location Change',
                                    `${t('proxies.details.locationChangeSummary', { country: countryName, city: cityName }) || `Move proxy to ${countryName} - ${cityName}`}\n${t('proxies.wizard.server4.protocol') || 'Protocol'}: ${locationProtocol.toUpperCase()}`,
                                    () => { void handleLocationChange(); }
                                  );
                                }}
                                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                                  String(pp.id) === locationParentProxy
                                    ? 'border-brand-accent bg-brand-accent/20'
                                    : 'border-neutral-dark bg-neutral-dark hover:border-brand-accent/50'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-neutral-light font-heading font-semibold text-sm">{pp.ip || '-'}</span>
                                  {pp.technology && (
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">{pp.technology}</span>
                                  )}
                                </div>
                                <div className="space-y-0.5 text-xs font-body">
                                  <div className="flex justify-between">
                                    <span className="text-neutral-medium">HTTP Port:</span>
                                    <span className="text-neutral-light font-mono">{pp.httpPort ?? '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-neutral-medium">SOCKS Port:</span>
                                    <span className="text-neutral-light font-mono">{pp.socksPort ?? '-'}</span>
                                  </div>
                                  {pp.rotationTime && (
                                    <div className="flex justify-between">
                                      <span className="text-neutral-medium">Rotation:</span>
                                      <span className="text-neutral-light">{pp.rotationTime}</span>
                                    </div>
                                  )}
                                  {pp.provider && (
                                    <div className="flex justify-between">
                                      <span className="text-neutral-medium">Provider:</span>
                                      <span className="text-neutral-light">{pp.provider}</span>
                                    </div>
                                  )}
                                  {pp.city && (
                                    <div className="flex justify-between">
                                      <span className="text-neutral-medium">City:</span>
                                      <span className="text-neutral-light">{pp.city}</span>
                                    </div>
                                  )}
                                  {pp.usage && (
                                    <div className="flex justify-between">
                                      <span className="text-neutral-medium">Usage:</span>
                                      <span className="text-neutral-light">{pp.usage}</span>
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleLocationBack}
                          className="w-full px-3 py-2 rounded-lg bg-neutral-dark text-neutral-light text-sm font-body hover:bg-neutral-dark/80 transition-all"
                        >
                          {t('common.back')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmAction(null);
        }}
        onConfirm={() => {
          confirmAction?.();
          setConfirmOpen(false);
          setConfirmAction(null);
        }}
        title={confirmTitle || t('common.confirm')}
        message={confirmMessage || t('common.confirm')}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
        isLoading={isRenewing || isUpdatingServer3 || isModifying5g || isChangingPkg5g}
      />
    </div>
  );
}

